import type { WalletType } from '@/lib/observability/shared/wallet-type'

import {
  getAnalyticsSessionId,
  identifyAnalyticsPerson,
  resetAnalyticsPerson,
  setAnalyticsPersonProperties,
  trackAnalyticsEvent
} from '../track'

interface WalletIdentifyParams {
  hashedAddress: string
  walletType: WalletType
  walletProvider: string
  chainId: number
  chainName: string
}

interface WalletEventParams {
  chainId: number
  chainName: string
  walletType: WalletType
  walletProvider: string
}

interface ChainSwitchedParams {
  fromChainId: number
  toChainId: number
  chainName: string
}

/** `hashedAddress` is the PostHog distinct_id — never pass a raw address. */
export function identifyWallet(params: WalletIdentifyParams) {
  identifyAnalyticsPerson(params.hashedAddress, {
    wallet_type: params.walletType,
    wallet_provider: params.walletProvider,
    chain_id: params.chainId,
    chain_name: params.chainName
  })
}

interface WalletTraits {
  walletType?: WalletType
  walletProvider?: string
  chainId?: number
  chainName?: string
}

/**
 * Corrects traits on the identified person once metadata that was still
 * resolving at identify time (contract detection, connector) settles, or
 * when the wallet switches chain.
 */
export function updateWalletTraits({
  walletType,
  walletProvider,
  chainId,
  chainName
}: WalletTraits) {
  const properties: Record<string, string | number> = {}
  if (walletType !== undefined) properties.wallet_type = walletType
  if (walletProvider !== undefined) properties.wallet_provider = walletProvider
  if (chainId !== undefined) properties.chain_id = chainId
  if (chainName !== undefined) properties.chain_name = chainName
  if (Object.keys(properties).length === 0) return

  setAnalyticsPersonProperties(properties)
}

/** Hashed wallets already sent as `wallet_connected` in `sessionId`, shared by every tab. */
const WALLET_CONNECTED_KEY = 'morpho.analytics.wallet_connected'

interface WalletConnectedMarker {
  sessionId: string
  wallets: string[]
}

/** Wallets already marked in `sessionId`; a marker from another session counts as empty. */
function readMarkedWallets(sessionId: string): string[] {
  try {
    const marker: Partial<WalletConnectedMarker> = JSON.parse(
      localStorage.getItem(WALLET_CONNECTED_KEY) ?? '{}'
    )
    return marker.sessionId === sessionId && Array.isArray(marker.wallets) ? marker.wallets : []
  } catch {
    return []
  }
}

/** Adds the wallet to the session's marker; false when it was already there. */
function markWalletConnected(sessionId: string, hashedAddress: string): boolean {
  const wallets = readMarkedWallets(sessionId)
  if (wallets.includes(hashedAddress)) return false
  const marker: WalletConnectedMarker = { sessionId, wallets: [...wallets, hashedAddress] }
  try {
    localStorage.setItem(WALLET_CONNECTED_KEY, JSON.stringify(marker))
  } catch {
    // Storage blocked: the event just stops being deduplicated.
  }
  return true
}

/**
 * Runs `claim` under a Web Lock shared by every tab, so two tabs mounting at
 * once can't both read the marker as empty. Runs it unlocked where the Web
 * Locks API is missing or the request fails.
 */
function withMarkerLock(claim: () => void) {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  if (!locks) return claim()

  let claimed = false
  const run = () => {
    if (claimed) return
    claimed = true
    claim()
  }
  locks.request(WALLET_CONNECTED_KEY, run).catch(run)
}

/**
 * Leaves the `wallet_connected` marker alone: `posthog.reset()` starts a new
 * session, which the marker is keyed by, and other tabs keep their entries.
 */
export function resetWalletIdentity() {
  resetAnalyticsPerson()
}

/**
 * Captures `wallet_connected` at most once per PostHog session and wallet.
 * Every page load and every tab mounts the web3 provider with the wallet
 * already connected, so without the marker each mount counted as a new
 * connection. A disconnect or account switch resets PostHog into a new
 * session, so the next connection counts again. Where the
 * Web Locks API exists the event is captured asynchronously, once the lock is held.
 */
export function trackWalletConnected(hashedAddress: string, params: WalletEventParams) {
  const capture = () =>
    trackAnalyticsEvent('wallet_connected', {
      chain_id: params.chainId,
      chain_name: params.chainName,
      wallet_type: params.walletType,
      wallet_provider: params.walletProvider
    })

  if (!getAnalyticsSessionId()) return capture()

  // Only the tab that adds the wallet to the marker captures the event. The
  // session is read inside the lock: a reset may rotate it while this tab waits.
  withMarkerLock(() => {
    const sessionId = getAnalyticsSessionId()
    if (!sessionId || markWalletConnected(sessionId, hashedAddress)) capture()
  })
}

/** Marks the new wallet as connected for this session, so a reload doesn't send `wallet_connected`. */
export function trackWalletAccountSwitched(hashedAddress: string, params: WalletEventParams) {
  withMarkerLock(() => {
    const sessionId = getAnalyticsSessionId()
    if (sessionId) markWalletConnected(sessionId, hashedAddress)
  })

  trackAnalyticsEvent('wallet_account_switched', {
    chain_id: params.chainId,
    chain_name: params.chainName,
    wallet_type: params.walletType,
    wallet_provider: params.walletProvider
  })
}

export function trackWalletDisconnected() {
  trackAnalyticsEvent('wallet_disconnected', {})
}

/**
 * Fired when a user flips "Allow offchain signature flows" in the wallet menu.
 * `is_enabled` is the state after the toggle. Each flip is one event, so count
 * unique persons on `offchain_signatures_toggled` with `is_enabled = false` to
 * get how many users turned permits off.
 */
export function trackOffchainSignaturesToggled({ isEnabled }: { isEnabled: boolean }) {
  trackAnalyticsEvent('offchain_signatures_toggled', { is_enabled: isEnabled })
}

export function trackChainSwitched(params: ChainSwitchedParams) {
  trackAnalyticsEvent('chain_switched', {
    from_chain_id: params.fromChainId,
    to_chain_id: params.toChainId,
    chain_name: params.chainName
  })
}
